import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import AdminLayout from '../components/AdminLayout';
import {
  BuildingOfficeIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ArrowLeftIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ChatBubbleBottomCenterTextIcon,
  ArrowUpIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from '../utils/toast';

const AdminOrganizationDetail = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { admin } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [agents, setAgents] = useState([]);
  const [expandedAgents, setExpandedAgents] = useState({});
  const [agentSessions, setAgentSessions] = useState({});
  const [expandedSessions, setExpandedSessions] = useState({});
  const [sessionMessages, setSessionMessages] = useState({});
  const [loadingSessions, setLoadingSessions] = useState({});
  const [loadingMessages, setLoadingMessages] = useState({});
  const [messagePagination, setMessagePagination] = useState({});
  const [messageSearchTerm, setMessageSearchTerm] = useState({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(1);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  const fetchOrganizationDetails = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      // Fetch organization details and agents
      const [orgResponse, agentsResponse] = await Promise.all([
        api.getOrganizationDetails(token, orgId),
        api.getOrganizationAgents(token, orgId)
      ]);
      
      setOrganization(orgResponse.data.organization);
      setAgents(agentsResponse.data.agents);
    } catch (error) {
      console.error('Failed to fetch organization details:', error);
      toast.error('Failed to load organization details');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchOrganizationDetails();
  }, [orgId, fetchOrganizationDetails]);

  const fetchAgentSessions = async (agentId) => {
    if (agentSessions[agentId] || loadingSessions[agentId]) return;
    
    try {
      setLoadingSessions(prev => ({ ...prev, [agentId]: true }));
      const token = localStorage.getItem('admin_token');
      const response = await api.getAgentSessions(token, agentId);
      setAgentSessions(prev => ({ ...prev, [agentId]: response.data.sessions }));
    } catch (error) {
      console.error('Failed to fetch agent sessions:', error);
      toast.error('Failed to load agent sessions');
    } finally {
      setLoadingSessions(prev => ({ ...prev, [agentId]: false }));
    }
  };

  const fetchSessionMessages = async (sessionId, page = 1, limit = 50) => {
    try {
      setLoadingMessages(prev => ({ ...prev, [sessionId]: true }));
      const token = localStorage.getItem('admin_token');
      const response = await api.getSessionMessages(token, sessionId, page, limit);
      
      if (page === 1) {
        // First load - replace messages
        setSessionMessages(prev => ({ ...prev, [sessionId]: response.data.messages }));
      } else {
        // Load more - append messages
        setSessionMessages(prev => ({ 
          ...prev, 
          [sessionId]: [...(prev[sessionId] || []), ...response.data.messages] 
        }));
      }
      
      setMessagePagination(prev => ({ 
        ...prev, 
        [sessionId]: response.data.pagination 
      }));
    } catch (error) {
      console.error('Failed to fetch session messages:', error);
      toast.error('Failed to load session messages');
    } finally {
      setLoadingMessages(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const fetchAvailablePackages = async () => {
    try {
      const response = await api.getPackages();
      setAvailablePackages(response.data.packages || []);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      toast.error('Failed to load available packages');
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/organizations/${orgId}/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setCurrentSubscription(data.subscription);
    } catch (error) {
      console.error('Failed to fetch current subscription:', error);
    }
  };

  const handleUpgradeOrganization = async () => {
    if (!selectedPackage) {
      toast.error('Please select a package');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/organizations/${orgId}/upgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          package_id: selectedPackage.id,
          admin_override: true,
          duration_months: selectedDuration
        })
      });

      toast.success(`Organization upgraded to ${selectedPackage.name} successfully`);
      setShowUpgradeModal(false);
      setSelectedPackage(null);
      setSelectedDuration(1);
      fetchCurrentSubscription(); // Refresh subscription data
    } catch (error) {
      console.error('Failed to upgrade organization:', error);
      toast.error('Failed to upgrade organization');
    }
  };

  const openUpgradeModal = () => {
    fetchAvailablePackages();
    fetchCurrentSubscription();
    setShowUpgradeModal(true);
  };

  const toggleAgentExpansion = (agentId) => {
    if (admin && admin.role !== "super_admin") {
      toast.error('You do not have permission to perform this action');
      return;
    }
    const isExpanded = expandedAgents[agentId];
    setExpandedAgents(prev => ({ ...prev, [agentId]: !isExpanded }));
    
    if (!isExpanded) {
      fetchAgentSessions(agentId);
    }
  };

  const toggleSessionExpansion = (sessionId) => {
    const isExpanded = expandedSessions[sessionId];
    setExpandedSessions(prev => ({ ...prev, [sessionId]: !isExpanded }));
    
    if (!isExpanded) {
      fetchSessionMessages(sessionId);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Verified
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="w-3 h-3 mr-1" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
            Unverified
          </span>
        );
    }
  };

  const getAgentStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="w-2 h-2 bg-green-500 rounded-full"></span>;
      case 'inactive':
        return <span className="w-2 h-2 bg-red-500 rounded-full"></span>;
      case 'connecting':
        return <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>;
      default:
        return <span className="w-2 h-2 bg-gray-400 rounded-full"></span>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="bg-gray-50 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading organization details...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!organization) {
    return (
      <AdminLayout>
        <div className="bg-gray-50 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h1 className="mt-4 text-xl font-semibold text-gray-900">Organization Not Found</h1>
            <button
              onClick={() => navigate('/admin/organizations')}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Back to Organizations
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={() => navigate('/admin/organizations')}
                  className="mr-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                </button>
                <div>
                  <div className="flex items-center space-x-3">
                    <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">{organization.name}</h1>
                      <div className="flex items-center space-x-4 mt-1">
                        {getStatusBadge(organization.verification_status)}
                        <span className="text-sm text-gray-500">
                          Created {formatDate(organization.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {admin?.role === 'super_admin' && (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={openUpgradeModal}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                  >
                    <ArrowUpIcon className="h-4 w-4 mr-2" />
                    Upgrade Package
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Organization Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <UserIcon className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{organization.user_count || 0}</p>
                  <p className="text-sm text-gray-600">Users</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <ChatBubbleLeftRightIcon className="h-8 w-8 text-whatsapp-600 mr-3" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{organization.client_count || 0}</p>
                  <p className="text-sm text-gray-600">WhatsApp Agents</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <CalendarDaysIcon className="h-8 w-8 text-purple-600 mr-3" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{organization.session_count || 0}</p>
                  <p className="text-sm text-gray-600">Sessions</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <ChatBubbleBottomCenterTextIcon className="h-8 w-8 text-green-600 mr-3" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{organization.message_count || 0}</p>
                  <p className="text-sm text-gray-600">Messages</p>
                </div>
              </div>
            </div>
          </div>

          {/* Agents List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">WhatsApp Agents & Sessions</h2>
              <p className="text-sm text-gray-600">Click on agents to view their sessions and messages</p>
            </div>
            
            <div className="divide-y divide-gray-200">
              {agents.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">No WhatsApp Agents</h3>
                  <p className="mt-2 text-gray-600">This organization hasn't created any WhatsApp agents yet.</p>
                </div>
              ) : (
                agents.map((agent) => (
                  <div key={agent.id} className="px-6 py-4">
                    {/* Agent Header */}
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                      onClick={() => toggleAgentExpansion(agent.id)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                          {expandedAgents[agent.id] ? (
                            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                          )}
                          {getAgentStatusBadge(agent.status)}
                        </div>
                        <ChatBubbleLeftRightIcon className="h-6 w-6 text-whatsapp-600" />
                        <div>
                          <p className="font-medium text-gray-900">{agent.client_id}</p>
                          <p className="text-sm text-gray-500">
                            {agent.whatsapp_number} • Created {formatDate(agent.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900 capitalize">{agent.status}</p>
                        <p className="text-xs text-gray-500">
                          Last active: {formatDate(agent.last_activity)}
                        </p>
                      </div>
                    </div>

                    {/* Agent Sessions */}
                    {expandedAgents[agent.id] && (
                      <div className="ml-10 mt-4 space-y-3">
                        {loadingSessions[agent.id] ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-whatsapp-600"></div>
                            <span className="ml-2 text-sm text-gray-600">Loading sessions...</span>
                          </div>
                        ) : agentSessions[agent.id]?.length === 0 ? (
                          <div className="text-center py-4 text-gray-500 text-sm">
                            No sessions messages found for this agent
                          </div>
                        ) : (
                          agentSessions[agent.id]?.map((session) => (
                            <div key={session.id} className="border border-gray-200 rounded-lg">
                              {/* Session Header */}
                              <div 
                                className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                                onClick={() => toggleSessionExpansion(session.id)}
                              >
                                <div className="flex items-center space-x-3">
                                  <div className="flex items-center space-x-2">
                                    {expandedSessions[session.id] ? (
                                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                                    ) : (
                                      <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                                    )}
                                    <CalendarDaysIcon className="h-5 w-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {session.is_group_chat ? 
                                        (session.group_name || session.chat_id) : 
                                        (session.contact_number || session.chat_id)
                                      }
                                    </p>
                                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                                      <span>{session.message_count} messages</span>
                                      {session.is_group_chat && (
                                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">
                                          Group
                                        </span>
                                      )}
                                      {session.ai_processed_count > 0 && (
                                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">
                                          {session.ai_processed_count} AI responses
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm text-gray-600">
                                    {formatDate(session.last_message_at)}
                                  </p>
                                </div>
                              </div>

                              {/* Session Messages */}
                              {expandedSessions[session.id] && (
                                <div className="border-t border-gray-200 bg-gray-50 p-4">
                                  {/* Session Info Header */}
                                  <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                      <div>
                                        <span className="font-medium text-gray-700">Contact:</span>
                                        <p className="text-gray-900">{session.contact_number || 'Unknown'}</p>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-700">Messages:</span>
                                        <p className="text-gray-900">
                                          {session.message_count} 
                                          {session.ai_processed_count > 0 && (
                                            <span className="text-sm text-gray-600 ml-1">
                                              ({session.ai_processed_count} with AI responses)
                                            </span>
                                          )}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-700">First Message:</span>
                                        <p className="text-gray-900">{formatDate(session.first_message_at)}</p>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-700">Last Activity:</span>
                                        <p className="text-gray-900">{formatDate(session.last_message_at)}</p>
                                      </div>
                                    </div>
                                  </div>

                                  {loadingMessages[session.id] ? (
                                    <div className="flex items-center justify-center py-8">
                                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-whatsapp-600"></div>
                                      <span className="ml-2 text-sm text-gray-600">Loading messages...</span>
                                    </div>
                                  ) : sessionMessages[session.id]?.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                      <ChatBubbleBottomCenterTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                                      <p className="text-sm">No messages found in this session</p>
                                    </div>
                                  ) : (
                                    <>
                                      {/* Messages Container */}
                                      <div className="bg-white rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
                                        <div className="p-4 space-y-3">
                                          {sessionMessages[session.id]?.map((message) => (
                                            <div key={message.id} className="flex flex-col space-y-3">
                                              {/* User Message (always show if content exists) */}
                                              {message.content && message.content.trim() !== '' && (
                                                <div className="flex justify-start">
                                                  <div className="max-w-xs lg:max-w-md">
                                                    <div className="bg-gray-100 rounded-lg px-3 py-2">
                                                      {/* Reply Context */}
                                                      {message.is_reply && message.quoted_message_content && (
                                                        <div className="mb-2 p-2 bg-gray-200 border-l-2 border-gray-400 rounded text-xs">
                                                          <p className="text-gray-600">Replying to:</p>
                                                          <p className="text-gray-800 italic">{message.quoted_message_content}</p>
                                                        </div>
                                                      )}
                                                      
                                                      <div className="text-sm prose prose-sm max-w-none">
                                                        <ReactMarkdown remarkPlugins={[remarkBreaks]}>{message.content}</ReactMarkdown>
                                                      </div>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-1 px-1">
                                                      <div className="flex items-center space-x-1">
                                                        <span className="text-xs text-gray-500">
                                                          {message.is_group_message ? message.participant_name || message.from_number : message.from_number}
                                                        </span>
                                                        {message.message_type !== 'text' && (
                                                          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                                                            {message.message_type}
                                                          </span>
                                                        )}
                                                      </div>
                                                      <p className="text-xs text-gray-500">
                                                        {formatMessageTime(message.sent_at)}
                                                      </p>
                                                    </div>
                                                  </div>
                                                </div>
                                              )}
                                              
                                              {/* AI Response (show if exists) */}
                                              {message.ai_response && message.ai_response.trim() !== '' && (
                                                <div className="flex justify-end">
                                                  <div className="max-w-xs lg:max-w-md">
                                                    <div className="bg-whatsapp-500 text-white rounded-lg px-3 py-2">
                                                      <div className="text-sm prose prose-sm max-w-none prose-invert">
                                                        <ReactMarkdown remarkPlugins={[remarkBreaks]}>{message.ai_response}</ReactMarkdown>
                                                      </div>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-1 px-1">
                                                      <div className="flex items-center space-x-2">
                                                        <span className="text-xs text-whatsapp-600 font-medium">AI Assistant</span>
                                                        {message.was_processed_by_ai && (
                                                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                                            Processed
                                                          </span>
                                                        )}
                                                      </div>
                                                      <p className="text-xs text-gray-500">
                                                        {formatMessageTime(message.sent_at)}
                                                      </p>
                                                    </div>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>

                                      {/* Pagination Controls */}
                                      {messagePagination[session.id] && messagePagination[session.id].total_pages > 1 && (
                                        <div className="mt-4 flex items-center justify-between">
                                          <div className="text-sm text-gray-600">
                                            Page {messagePagination[session.id].page} of {messagePagination[session.id].total_pages} 
                                            ({messagePagination[session.id].total_count} total messages)
                                          </div>
                                          <div className="flex space-x-2">
                                            {messagePagination[session.id].has_prev && (
                                              <button
                                                onClick={() => fetchSessionMessages(session.id, messagePagination[session.id].page - 1)}
                                                disabled={loadingMessages[session.id]}
                                                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                                              >
                                                Previous
                                              </button>
                                            )}
                                            {messagePagination[session.id].has_next && (
                                              <button
                                                onClick={() => fetchSessionMessages(session.id, messagePagination[session.id].page + 1)}
                                                disabled={loadingMessages[session.id]}
                                                className="px-3 py-1 text-sm bg-whatsapp-100 text-whatsapp-700 rounded hover:bg-whatsapp-200 disabled:opacity-50"
                                              >
                                                Load More
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Upgrade Organization Modal */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Upgrade Organization: {organization?.name}
                  </h3>
                  <button
                    onClick={() => setShowUpgradeModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Current Subscription Info */}
                {currentSubscription && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Current Subscription</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-blue-700">Package:</span>
                        <span className="ml-2 text-blue-900">{currentSubscription.package_name}</span>
                      </div>
                      <div>
                        <span className="text-blue-700">Status:</span>
                        <span className="ml-2 text-blue-900 capitalize">{currentSubscription.status}</span>
                      </div>
                      <div>
                        <span className="text-blue-700">Expires:</span>
                        <span className="ml-2 text-blue-900">
                          {currentSubscription.expires_at ? formatDate(currentSubscription.expires_at) : 'Never'}
                        </span>
                      </div>
                      <div>
                        <span className="text-blue-700">Price:</span>
                        <span className="ml-2 text-blue-900">
                          ${currentSubscription.amount_usd}/{currentSubscription.billing_cycle}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Available Packages */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Available Packages</h4>
                  {availablePackages.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-4 text-lg font-medium text-gray-900">No packages available</h3>
                      <p className="mt-2 text-gray-600">Please create packages first in the package management section.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {availablePackages.map((pkg) => (
                        <div
                          key={pkg.id}
                          className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                            selectedPackage?.id === pkg.id
                              ? 'border-green-500 bg-green-50'
                              : currentSubscription?.package_id === pkg.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setSelectedPackage(pkg)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <h5 className="text-lg font-semibold text-gray-900">{pkg.name}</h5>
                                {pkg.is_recommended && (
                                  <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Recommended
                                  </span>
                                )}
                                {currentSubscription?.package_id === pkg.id && (
                                  <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Current
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-1">{pkg.description}</p>

                              <div className="mt-3">
                                <div className="text-lg font-bold text-gray-900">
                                  ${pkg.price_usd}
                                  <span className="text-sm font-normal text-gray-600">/{pkg.billing_cycle}</span>
                                </div>
                                {pkg.price_local && (
                                  <div className="text-sm text-gray-600">
                                    {pkg.price_local} {pkg.local_currency}
                                  </div>
                                )}
                              </div>
                            </div>

                            {selectedPackage?.id === pkg.id && (
                              <CheckCircleIcon className="h-6 w-6 text-green-600" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {selectedPackage && (
                  <div className="mt-6">
                    <label htmlFor="duration-months" className="block text-sm font-medium text-gray-700 mb-2">
                      Subscription Duration (months)
                    </label>
                    <input
                      id="duration-months"
                      type="number"
                      min={1}
                      max={24}
                      value={selectedDuration}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        if (!isNaN(val)) setSelectedDuration(Math.min(24, Math.max(1, val)));
                      }}
                      className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">Enter 1–24 months (e.g. 4, 8, 18)</p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowUpgradeModal(false);
                      setSelectedPackage(null);
                      setSelectedDuration(1);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpgradeOrganization}
                    disabled={!selectedPackage}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Upgrade Organization
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOrganizationDetail;
